import { useState, useRef, useCallback, useEffect } from 'react';

import {
    connectToEngine,
    disconnectFromEngine,
    sendMessageToEngine,
    isConnectedToEngine,
    createNewEngineConversation,
    deleteConversationFromEngine,
} from '@/shared/services/engineChatService';
import { useChatPageState } from '@/shared/contexts/ChatPageContext';

const WIDGET_GREETING = {
    id: 'widget-greeting',
    text: "Hi! I'm Bili, your sustainability assistant. Ask me about environmental data, water resources, or sustainability topics in Colorado.",
    sender: 'bot',
    timestamp: new Date(),
};

/**
 * Custom hook managing the floating chat widget lifecycle.
 * - Lazy connection: connects only when widget opens
 * - Conversation persists across page navigation
 * - Cleans up on explicit close (disconnect) unless promoted to BiliChat
 */
export default function useWidgetChat() {
    const [messages, setMessages] = useState([WIDGET_GREETING]);
    const [loading, setLoading] = useState(false);
    const [threadId, setThreadId] = useState(null);

    const messagesEndRef = useRef(null);
    const connectingRef = useRef(false);
    const promotedRef = useRef(false);
    const hasUserMessageRef = useRef(false);
    const { pageState } = useChatPageState();

    // Scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const connect = useCallback(async () => {
        if (isConnectedToEngine() || connectingRef.current) return;
        connectingRef.current = true;

        try {
            await connectToEngine();
            const result = await createNewEngineConversation();
            setThreadId(result.threadId);
            hasUserMessageRef.current = false;
        } catch {
            // connection failure is surfaced via loading state / send error
        } finally {
            connectingRef.current = false;
        }
    }, []); // stable reference — connectingRef doesn't trigger re-renders

    const disconnect = useCallback(() => {
        disconnectFromEngine();

        // Only delete truly abandoned conversations: no user messages and never
        // promoted. A chat the user actually typed in is preserved so it reaches
        // their BiliChat history even on a plain X-button close (bug #1010).
        if (threadId && !promotedRef.current && !hasUserMessageRef.current) {
            deleteConversationFromEngine(threadId).catch(() => {});
        }
        setThreadId(null);
        promotedRef.current = false;
        hasUserMessageRef.current = false;
    }, [threadId]);

    const send = useCallback(
        async (text) => {
            if (!text.trim() || loading) return;

            const userMessage = {
                id: `user-${Date.now()}`,
                text,
                sender: 'user',
                timestamp: new Date(),
            };
            setMessages((prev) => [...prev, userMessage]);
            hasUserMessageRef.current = true;
            setLoading(true);

            try {
                // Connect if not already connected
                if (!isConnectedToEngine()) {
                    await connectToEngine();
                    const result = await createNewEngineConversation();
                    setThreadId(result.threadId);
                }

                const response = await sendMessageToEngine(text, {
                    page_context: pageState,
                });
                const botMessage = {
                    id: response.id || `bot-${Date.now()}`,
                    text: response.text,
                    sender: 'bot',
                    timestamp: response.timestamp || new Date(),
                    // Map payload from the build_map_payload node. This is an
                    // explicit field list, not a spread — any new field returned
                    // by sendMessageToEngine has to be copied here or it is
                    // silently dropped before render. Mirrored in BiliChat.jsx.
                    map: response.map,
                };
                setMessages((prev) => [...prev, botMessage]);
            } catch (err) {
                const errorMessage = {
                    id: `error-${Date.now()}`,
                    text: `Sorry, I encountered an error: ${err.message || 'Please try again.'}`,
                    sender: 'bot',
                    timestamp: new Date(),
                    isError: true,
                };
                setMessages((prev) => [...prev, errorMessage]);
            } finally {
                setLoading(false);
            }
        },
        [loading, pageState]
    );

    // Mark conversation as promoted so it isn't deleted on disconnect
    const promote = useCallback(() => {
        promotedRef.current = true;
    }, []);

    return {
        messages,
        loading,
        messagesEndRef,
        threadId,
        connect,
        disconnect,
        send,
        promote,
    };
}
